/**
 * Runtime JSON Schema → Zod converter.
 *
 * Mirrors the compile-time zodType Handlebars helper in
 * generator/template-engine.ts but produces live z.ZodType
 * objects instead of code strings.
 */

import { z } from "zod";

function applyStringValidations(schema: z.ZodString, def: Record<string, unknown>): z.ZodType {
  let s: z.ZodString = schema;
  if (typeof def.minLength === "number") s = s.min(def.minLength);
  if (typeof def.maxLength === "number") s = s.max(def.maxLength);
  if (typeof def.pattern === "string") s = s.regex(new RegExp(def.pattern));
  return s;
}

function applyNumberValidations(schema: z.ZodNumber, def: Record<string, unknown>, isInteger: boolean): z.ZodType {
  let n: z.ZodNumber = schema;
  if (isInteger) n = n.int();
  if (typeof def.minimum === "number") n = n.min(def.minimum);
  if (typeof def.maximum === "number") n = n.max(def.maximum);
  return n;
}

function applyArrayValidations(schema: z.ZodArray<z.ZodType>, def: Record<string, unknown>): z.ZodType {
  let a: z.ZodArray<z.ZodType> = schema;
  if (typeof def.minItems === "number") a = a.min(def.minItems);
  if (typeof def.maxItems === "number") a = a.max(def.maxItems);
  return a;
}

/**
 * Convert a JSON Schema definition to a Zod schema object at runtime.
 *
 * Supports the same subset as the compile-time zodType helper:
 * object, array, string, number, integer, boolean, enum, const, allOf.
 */
export function jsonSchemaToZod(schema: Record<string, unknown>): z.ZodType {
  if (!schema || typeof schema !== "object") return z.unknown();

  if (schema.enum) {
    const vals = schema.enum as [string, ...string[]];
    if (vals.length === 0) return z.unknown();
    return z.enum(vals);
  }

  if (schema.const !== undefined) {
    const val = schema.const;
    if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
      return z.literal(val);
    }
    return z.unknown();
  }

  const type = schema.type as string | undefined;

  if (type === "string") return applyStringValidations(z.string(), schema);
  if (type === "integer") return applyNumberValidations(z.number(), schema, true);
  if (type === "number") return applyNumberValidations(z.number(), schema, false);
  if (type === "boolean") return z.boolean();

  if (type === "array") {
    const items = schema.items as Record<string, unknown> | undefined;
    const inner = items ? jsonSchemaToZod(items) : z.unknown();
    return applyArrayValidations(z.array(inner), schema);
  }

  if (type === "object") {
    const props = schema.properties as Record<string, Record<string, unknown>> | undefined;
    if (!props) return z.record(z.string(), z.unknown());

    const required = new Set((schema.required as string[]) ?? []);
    const shape: Record<string, z.ZodType> = {};
    for (const [key, val] of Object.entries(props)) {
      const fieldSchema = jsonSchemaToZod(val);
      shape[key] = required.has(key) ? fieldSchema : fieldSchema.optional();
    }
    return z.object(shape).passthrough();
  }

  if (schema.allOf) {
    const parts = (schema.allOf as Record<string, unknown>[]).map(jsonSchemaToZod);
    if (parts.length === 0) return z.unknown();
    if (parts.length === 1) return parts[0];
    let merged = parts[0];
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (merged instanceof z.ZodObject && part instanceof z.ZodObject) {
        merged = merged.merge(part);
      }
    }
    return merged;
  }

  return z.unknown();
}
